import { v4 as uuid } from 'uuid'

// Frontend form fields - exact match to CONTRIBUTOR_FIELDS in lvc-create-contributor-info.ts
export interface ContributorInfoData {
  nameFr: string
  nameEn: string
  contributorType: 'Person' | 'Organization'
  alternateName: string
  descriptionFr: string
  descriptionEn: string
  mainEntityOfPageFr: string
  mainEntityOfPageEn: string
  nationality: string
  birthplace: string
  workplace: string
  postalAddress: string // Will be replaced with Mapbox autofill object
  wikidataId: string
  isni: string
  shopifyUrl: string
  appleUrl: string
  youtubeUrl: string
}

// Mapbox address autofill provides structured address fields
export interface ContributorAddress {
  streetAddress?: string // from 'street-address'
  addressLine2?: string // from 'address-line2'
  addressLocality?: string // from 'address-level2' (city)
  addressRegion?: string // from 'address-level1' (province)
  postalCode?: string // from 'postal-code'
}

export interface ContributorImageData {
  imageUrl?: string
  royaltyCheckbox?: boolean
  imageCredit?: string
  altTextFr?: string
  altTextEn?: string
}

export interface ContributorFlowData {
  createContributorInfo?: ContributorInfoData & { postalAddressData?: ContributorAddress } // Address data embedded in info step
  createContributorAddImage?: ContributorImageData
}

export function transformFlowDataToContributorFormat(flowData: ContributorFlowData): {
  contributorId: string
  contributorType: string
  nameFr: string
  nameEn: string
  descriptionFr: string
  descriptionEn: string
  alternateName: string
  mainEntityOfPageFr: string
  mainEntityOfPageEn: string
  nationality: string
  birthplace: string
  workplace: string
  wikidataId: string
  isni: string
  shopifyUrl: string
  appleUrl: string
  youtubeUrl: string
  postalAddress?: {
    streetAddress?: string
    addressLocality?: string
    addressRegion?: string
    postalCode?: string
  }
  imageUrl: string // REQUIRED
  royaltyCheckbox: boolean
  imageCredit?: string
  altTextFr?: string
  altTextEn?: string
} {
  type ContributorResult = ReturnType<typeof transformFlowDataToContributorFormat>
  const info: ContributorInfoData & { postalAddressData?: ContributorAddress } =
    flowData.createContributorInfo || {
      nameFr: '',
      nameEn: '',
      contributorType: 'Person',
      alternateName: '',
      descriptionFr: '',
      descriptionEn: '',
      mainEntityOfPageFr: '',
      mainEntityOfPageEn: '',
      nationality: '',
      birthplace: '',
      workplace: '',
      postalAddress: '',
      wikidataId: '',
      isni: '',
      shopifyUrl: '',
      appleUrl: '',
      youtubeUrl: '',
    }
  const address = info.postalAddressData
  const image: ContributorImageData = flowData.createContributorAddImage || {}

  const result: ContributorResult = {
    contributorId: uuid(), // Generate unique ID
    contributorType: info.contributorType || 'Person',
    nameFr: info.nameFr || '',
    nameEn: info.nameEn || '',
    descriptionFr: info.descriptionFr || '',
    descriptionEn: info.descriptionEn || '',
    alternateName: info.alternateName || '',
    mainEntityOfPageFr: info.mainEntityOfPageFr || '',
    mainEntityOfPageEn: info.mainEntityOfPageEn || '',
    nationality: info.nationality || '',
    birthplace: info.birthplace || '',
    workplace: info.workplace || '',
    wikidataId: info.wikidataId || '',
    isni: info.isni || '',
    shopifyUrl: info.shopifyUrl || '',
    appleUrl: info.appleUrl || '',
    youtubeUrl: info.youtubeUrl || '',
    // Image is REQUIRED
    imageUrl: image.imageUrl || '',
    royaltyCheckbox: image.royaltyCheckbox || false,
    imageCredit: image.imageCredit || '',
    altTextFr: image.altTextFr || '',
    altTextEn: image.altTextEn || '',
  }

  // Only include address for Organization type
  if (
    info.contributorType === 'Organization' &&
    address &&
    (address.streetAddress || address.addressLocality)
  ) {
    result.postalAddress = {
      streetAddress: address.streetAddress,
      addressLocality: address.addressLocality,
      addressRegion: address.addressRegion || 'QC',
      postalCode: address.postalCode,
    }
  }

  return result
}
